import { SiteUrls } from '@tableau/shared-urls';
import { CheckBoxWidget, TabStyles } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { CommonStyles } from '~common/react/CommonStyles';
import { ActionsMenu, ActionsMenuItem } from '~common/react/components/SharedWidgetsActionMenu';
import { imgUrl } from '~common/utilities/ImgUrl';
import { Formatters, Messages } from '~src/codegen/Localize';
import { IPublishedConnectionDetailInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import {
  getPublishedConnectionExtractRefreshLevel,
  isExtractSSSEnabled
} from '~src/publishedConnection/utils/PublishedConnectionTaskUtil';
import { ISchedule } from '~src/schedule/types/ISchedule';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { classes, cssClass, flexColumnStyle, flexRowStyle, textEllipsis } from '~src/style';
import { IExtractTaskInfo } from '~src/ts/models/IExtractTaskInfo';

import { PublishedConnectionExtractRefreshLevel } from '../../types/PublishedConnectionExtractRefreshLevel';
import { PublishedConnectionScheduledTaskTablesGrid } from './PublishedConnectionScheduledTaskTablesGrid';
import { PublishedConnectionTaskActions } from './PublishedConnectionTaskActions';

const checkboxAreaWidth = 26;
const cssPrefix = 'PublishedConnectionScheduledTaskItem';

const rootClass = cssClass(cssPrefix, 'root', flexColumnStyle, {
  marginBottom: CommonStyles.Sizes.LargeSpace,
  border: `1px solid ${CommonStyles.Colors.Grey203}`,
  paddingBottom: 1,
  backgroundColor: CommonStyles.Colors.White
});

const taskInfo = cssClass(cssPrefix, 'taskInfo', {
  display: 'flex',
  alignItems: 'center',
});

const checkboxWrapperClass = cssClass(cssPrefix, 'checkboxWrapper', {
  display: 'flex',
  paddingRight: CommonStyles.Sizes.MediumSpace,
  height: '100%',
  alignItems: 'center'
});

const selectedClass = cssClass(cssPrefix, 'selected', {
  backgroundColor: CommonStyles.Colors.Grey235
});

const scheduleClass = cssClass(cssPrefix, 'schedule', {
  padding: `${CommonStyles.Sizes.MediumSpace}px 0 ${CommonStyles.Sizes.MediumSpace}px ${CommonStyles.Sizes.MediumSpace}px`,
});

const scheduleHeaderClass = cssClass(cssPrefix, 'scheduleHeader', flexRowStyle);

const scheduleLinkClass = cssClass(cssPrefix, 'scheduleLink', textEllipsis, {
  fontSize: CommonStyles.Sizes.LargeFontSize,
  maxWidth: 400
});

const scheduleDescriptionClass = cssClass(cssPrefix, 'scheduleDescription', flexRowStyle, {
  fontWeight: 'normal',
  color: CommonStyles.Colors.Grey89
});

const scheduleDescriptionPaddingForCheckboxClass = cssClass(cssPrefix, 'paddingForCheckbox', {
  paddingLeft: checkboxAreaWidth
});

const actionMenuClass = cssClass(cssPrefix, 'actionMenu', {
  marginLeft: CommonStyles.Sizes.DefaultSpace,
  position: 'relative',
  bottom: CommonStyles.Sizes.SmallSpace
});

const actionButtonStyle: React.CSSProperties = {
  padding: `0 ${CommonStyles.Sizes.MediumSpace}px`,
  minWidth: 0
};

const infoTextClass = cssClass(cssPrefix, 'infoText', {
  padding: `0 ${CommonStyles.Sizes.MediumSpace}px`,
});

const tableCountClass = cssClass(cssPrefix, 'tableCount', flexRowStyle, {
  border: `${CommonStyles.Focus.IndicatorWidth}px solid transparent`,
  boxSizing: 'border-box',
  cursor: 'pointer',
  marginLeft: 'auto',
  marginRight: CommonStyles.Sizes.MediumSpace,
  textDecoration: 'none',
  color: TabStyles.Typography.Color.Primary,
  $nest: {
    '&:focus': {
      ...CommonStyles.Focus.Indicator,
      textDecoration: 'none',
      color: TabStyles.Typography.Color.Primary,
    }
  }
});

const expandIcon = cssClass(cssPrefix, 'expandIcon', {
  width: 20,
  height: 16,
  cursor: 'pointer',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
});

const iconToggleExpandUp = cssClass(cssPrefix, 'dropdownUp', {
  backgroundImage: imgUrl('expand_close.png')
});
const iconToggleExpandDown = cssClass(cssPrefix, 'dropdownDown', {
  backgroundImage: imgUrl('expand_open.png')
});

function renderCheckbox(
  editActionsAllowed: boolean,
  selected: boolean,
  handleChange: (checked: boolean) => void,
  id: string
): JSX.Element | null {
  if (editActionsAllowed) {
    return (
      <div className={checkboxWrapperClass}>
        <CheckBoxWidget
          checked={selected}
          handleChange={handleChange}
          testId={'published-connection-run-schedule-item-' + id}
          removeLeftMargin={true}
        />
      </div>
    );
  } else {
    return null;
  }
}

function renderScheduleText(
  isServerOrSiteAdmin: boolean,
  scheduleTaskUrl: string,
  schedule: ISchedule
): JSX.Element {
  const selfServiceEnabled = isExtractSSSEnabled();
  const scheduleName = selfServiceEnabled
    ? IScheduleInfo.getRecurrenceDescription(IScheduleInfo.toSSSRecurrence(schedule))
    : schedule.name;

  return (isServerOrSiteAdmin && !selfServiceEnabled)
    ? (<a className={scheduleLinkClass}
          href={scheduleTaskUrl}
          title={scheduleName}
          data-tb-test-id={'published-connection-run-schedule-item-link'}
      >
        {scheduleName}
      </a>
    )
    : (
      <div
        className={scheduleLinkClass}
        title={scheduleName}
        data-tb-test-id={'published-connection-run-schedule-item-link'}
      >
        {scheduleName}
      </div>
    );
}


function renderActionsMenu(
  actions: ReadonlyArray<ActionsMenuItem>
): JSX.Element | null {
  if (actions.length > 0) {
    return (
      <div className={actionMenuClass}>
        <ActionsMenu
          actions={actions}
          buttonLabel={'\u2022\u2022\u2022'}
          style={actionButtonStyle}
        />
      </div>
    );
  } else {
    return null;
  }
}

function renderTaskInfo(
  task: IExtractTaskInfo
): JSX.Element {
  return (
    <>
      <div className={infoTextClass}>
        {(getPublishedConnectionExtractRefreshLevel(task.extractServiceTask.transactionLevel)
          === PublishedConnectionExtractRefreshLevel.Database)
          ? Messages.publishedConnectionTableSyncRefresh()
          : Messages.publishedConnectionTableIndependentRefresh()
        }
      </div>
      <div className={infoTextClass}>{Messages.writeOptionsFull()}</div>
    </>
  );
}

export type PublishedConnectionScheduledTaskItemProps = Readonly<{
  collapsed: boolean;
  editActionsAllowed: boolean;
  isServerOrSiteAdmin: boolean;
  onCheckboxClick: (task: IExtractTaskInfo, selected: boolean) => void;
  onToggleCollapse: (id: string) => void;
  onUpdateTask: () => void;
  publishedConnectionInfo: IPublishedConnectionDetailInfo;
  selected: boolean;
  task: IExtractTaskInfo;
}>;

export function PublishedConnectionScheduledTaskItem(props: PublishedConnectionScheduledTaskItemProps): JSX.Element {
  const {
    collapsed,
    editActionsAllowed,
    isServerOrSiteAdmin,
    onCheckboxClick,
    onToggleCollapse,
    onUpdateTask,
    publishedConnectionInfo,
    selected,
    task
  } = props;

  const scheduleUrl = React.useMemo(() => {
      const siteUrls = new SiteUrls({
        siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName(),
        queryString: window.location.search
      });

      return siteUrls.scheduleExtractRefreshesUrl(task.schedule.id);
    },
    [task.schedule.id]
  );

  const actions = React.useMemo(() => {
      if (editActionsAllowed) {
        return PublishedConnectionTaskActions.getTasksActions(
          [task],
          publishedConnectionInfo,
          onUpdateTask
        );
      } else {
        return [];
      }
    },
    [editActionsAllowed, onUpdateTask, publishedConnectionInfo, task]
  );

  const handleClick = React.useCallback((checked: boolean) => {
      onCheckboxClick(task, checked);
    },
    [onCheckboxClick, task]
  );

  const toggleItemCollapse = React.useCallback(() => {
      onToggleCollapse(task.id);
    },
    [onToggleCollapse, task]
  );

  const taskTables = React.useMemo(() => {
      const pconnTables = _.indexBy(publishedConnectionInfo.tables, 'luid');
      return _.compact(task.extractServiceTask?.startingItemIds?.map(tableLuid => pconnTables[tableLuid])) || [];
    },
    [publishedConnectionInfo.tables, task.extractServiceTask?.startingItemIds]
  );

  const renderOutputCountAndExpando = React.useMemo((): JSX.Element => {
      const count = taskTables?.length || 0;
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          className={tableCountClass}
          onClick={toggleItemCollapse}
          href='javascript:void(0)'
        >
          {Messages.publishedConnectionScheduledTaskTablesCount({
            COUNT: count,
            COUNT_LOC: Formatters.formatCount(count)
          })}
          <div className={classes(expandIcon, collapsed ? iconToggleExpandDown : iconToggleExpandUp)}/>
        </a>
      );
    },
    [collapsed, taskTables, toggleItemCollapse]
  );

  return (
    <div className={rootClass}>
      <div className={selected ? classes(selectedClass, taskInfo) : taskInfo}>
        <div className={scheduleClass}>
          <div className={scheduleHeaderClass}>
            {renderCheckbox(editActionsAllowed, selected, handleClick, task.id)}
            {renderScheduleText(isServerOrSiteAdmin, scheduleUrl, task.schedule)}
            {renderActionsMenu(actions)}
            {renderTaskInfo(task)}
          </div>
          <div
            className={classes(scheduleDescriptionClass, editActionsAllowed && scheduleDescriptionPaddingForCheckboxClass)}>
            {IScheduleInfo.getScheduleDescription(task.schedule)}
          </div>
        </div>
        {renderOutputCountAndExpando}
      </div>
      {collapsed ? null : <PublishedConnectionScheduledTaskTablesGrid tables={taskTables}/>}
    </div>
  );
}
